import { RefObject, useLayoutEffect, useState } from "react";
import { flushSync } from "react-dom";

const countRows = (el: HTMLElement) =>
  new Set(Array.from(el.children, (child) => (child as HTMLElement).offsetTop)).size;

/**
 * Whether the children of a wrapping flex container fill `rows` or more rows.
 * Measures before the first paint and again whenever the container resizes;
 * the state only changes when the answer flips.
 */
export function useWrapsToRows(ref: RefObject<HTMLElement>, rows: number): boolean {
  const [wraps, setWraps] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWraps(countRows(el) >= rows);
    // jsdom has none; there the container never wraps anyway.
    if (typeof ResizeObserver === "undefined") return;
    // Resize callbacks run before paint; commit in the same frame so the old layout never shows.
    const observer = new ResizeObserver(() => flushSync(() => setWraps(countRows(el) >= rows)));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rows]);

  return wraps;
}
